/**
 * System Status Bar — Slim HUD strip at top of all mobile pages.
 * Shows system status, readiness %, and risk level in monospace.
 */
import { cn } from '@/lib/utils';
import { useDashboardData } from '@/hooks/useDashboardData';
import { adaptLegacyDecision } from '@/lib/ai/adapters/adaptLegacyDecision';

interface CoachDecisionLike {
  version?: string;
  confidence?: number | { score?: number; explanation?: string };
  decision?: string;
  recommendation?: string;
  explanation?: string;
}

function getReadinessFromDecision(data: CoachDecisionLike | null | undefined): { pct: number; risk: string } {
  if (!data) return { pct: 0, risk: '—' };

  // Try v2 format
  if (data.version === 'coach_decision_v2') {
    const conf = typeof data.confidence === 'number' ? data.confidence : 0.8;
    const pctVal = Math.round(conf * 100);
    const decision = (data.decision || '').toUpperCase();
    const risk = decision === 'REST' || decision === 'CANCEL' ? 'HIGH' : decision === 'MODIFY' ? 'MED' : 'LOW';
    return { pct: pctVal, risk };
  }

  // Try v1
  if (data.recommendation) {
    const v2 = adaptLegacyDecision({
      recommendation: data.recommendation,
      explanation: data.explanation || '',
      confidence: typeof data.confidence === 'object' && data.confidence ? data.confidence as { score: number; explanation: string } : { score: 0.8, explanation: '' },
    });
    const conf = typeof v2.confidence === 'number' ? v2.confidence : 0.8;
    const pctVal = Math.round(conf * 100);
    const decision = v2.decision;
    const risk = decision === 'REST' || decision === 'CANCEL' ? 'HIGH' : decision === 'MODIFY' ? 'MED' : 'LOW';
    return { pct: pctVal, risk };
  }

  // confidence object
  const score = typeof data.confidence === 'object' ? data.confidence?.score : typeof data.confidence === 'number' ? data.confidence : null;
  if (score !== null && score !== undefined) {
    return { pct: Math.round(score * 100), risk: score >= 0.7 ? 'LOW' : score >= 0.4 ? 'MED' : 'HIGH' };
  }

  return { pct: 0, risk: '—' };
}

export function SystemStatusBar() {
  const { todayIntelligence, todayIntelligenceLoading } = useDashboardData();

  const { pct, risk } = getReadinessFromDecision(todayIntelligence);
  const riskColor = risk === 'LOW' ? 'text-green-400' : risk === 'MED' ? 'text-amber-400' : risk === 'HIGH' ? 'text-red-400' : 'text-slate-500';

  return (
    <div className="lg:hidden fixed top-[calc(3.5rem+env(safe-area-inset-top,0px))] left-0 right-0 z-40 h-6 flex items-center justify-center gap-4 border-b border-white/[0.04] bg-[#0B0E14]/95 backdrop-blur-md">
      <span className="flex items-center gap-1.5">
        <span className="telemetry-dot telemetry-dot--green" style={{ width: 4, height: 4 }} />
        <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-500">
          SYS: ACTIVE
        </span>
      </span>
      <span className="w-px h-3 bg-white/[0.08]" />
      <span className="text-[8px] font-mono tracking-[0.12em] uppercase text-slate-400">
        RDY: {todayIntelligenceLoading ? '...' : `${pct}%`}
      </span>
      <span className="w-px h-3 bg-white/[0.08]" />
      <span className={cn('text-[8px] font-mono tracking-[0.12em] uppercase', riskColor)}>
        RISK: {todayIntelligenceLoading ? '...' : risk}
      </span>
    </div>
  );
}
